import { Button, Icon, Input, Modal, Table } from "semantic-ui-react";
import * as React from "react";
import { useState } from "react";
import "./compensation-new.css"
import {
    Form, Params, useLoaderData, useNavigate
} from "react-router-dom";
import {
    CreationProject,
    ICmiDocumentationRawi,
    ICmiDocumentationRawiUwe,
    Project,
    UsageProject
} from "../../../stores/project/project";
import { RootStore } from "../../../stores/root-store";
import { Constants } from "../../../globals/constants";
import FilterConfiguration from "../../../components/filter-configuration/filter-configuration";
import { useItemFilter } from "../../../hooks/use-item-filter";
import "./compensation-new-select-creation-project.css";
import { getLabel } from "../../../reflection/reflection";

type LoadResult = {
    usageProject: UsageProject;
    creationProjects: CreationProject[];
    municipalityNames: string[];
    projectTypes: string[];
    status: string[];
    actionStatus: string[];
}

function projectSearchFilter(project: Project, searchText: string) {
    const searchTextLowerCase = searchText.toLowerCase();

    return (
        project.id.toString().includes(searchTextLowerCase) ||
        project.name.toLowerCase().includes(searchTextLowerCase) ||
        project.municipalityName.toLowerCase().includes(searchTextLowerCase) ||
        project.ebageNr?.toLowerCase().includes(searchTextLowerCase) ||
        ("cmiNumberRawi" in project && (project as ICmiDocumentationRawi).cmiNumberRawi?.toLowerCase().includes(searchTextLowerCase)) ||
        ("cmiDocumentsRawiPre" in project && (
            (project as ICmiDocumentationRawiUwe).cmiDocumentsRawiPre.map(e => e.toLowerCase()).some(e => e.includes(searchTextLowerCase)) ||
            (project as ICmiDocumentationRawiUwe).cmiDocumentsRawiApproved.map(e => e.toLowerCase()).some(e => e.includes(searchTextLowerCase)) ||
            (project as ICmiDocumentationRawiUwe).cmiDocumentsUwePre.map(e => e.toLowerCase()).some(e => e.includes(searchTextLowerCase)) ||
            (project as ICmiDocumentationRawiUwe).cmiDocumentsUweApproved.map(e => e.toLowerCase()).some(e => e.includes(searchTextLowerCase))
        ))
    );
}

export async function loader(
    request: Request,
    params: Params,
    rootStore: RootStore
) {
    if (params.projectId !== undefined) {
        const usageProjectId = parseInt(params.projectId) || 0;
        const rootStoreSession = rootStore.newSession();
        const municipalityNames = (await rootStoreSession.municipalityStore.loadAll()).map(m => m.name);
        const usageProject = await rootStoreSession.projectStore.loadUsageProject(usageProjectId);
        await rootStoreSession.compensationStore.loadWithOptions({ usageProjectId: usageProjectId });
        const creationProjects = (await rootStoreSession.projectStore.loadAllCreationProjects()).filter(p => p.typeKey !== Constants.projectTypeRecultivation);
        await rootStoreSession.areaStore.loadAllCreationAreas();
        const projectTypes = rootStoreSession.keyValueStore.projectTypes.map(i => i.value);
        const status = rootStoreSession.keyValueStore.projectStatuses.map(i => i.value);
        const actionStatus = rootStoreSession.keyValueStore.actionStatus.map(i => i.value);

        return { usageProject, creationProjects, municipalityNames, projectTypes, status, actionStatus };
    }

    throw new Response("", {
        status: 404,
        statusText: "Beanspruchungsprojekt wurde nicht gefunden",
    });
}

export default function CompensationNewSelectCreationProject(): JSX.Element {
    const [selectedCreationProject, setSelectedCreationProject] = useState<CreationProject>();
    const { usageProject, creationProjects, municipalityNames, projectTypes, status, actionStatus } = useLoaderData() as LoadResult;
    const navigate = useNavigate();
    const [searchText, setSearchText] = useState<string>("");
    const [state, dispatch] = useItemFilter<CreationProject>("name", "ascending", creationProjects, []);
    const { sortAttribute, sortDirection, items } = state;
    const filterOptions = [
        {
            attribute: "fullType",
            attributeDisplayText: getLabel(CreationProject.prototype, "fullType"),
            values: projectTypes
        },
        {
            attribute: "municipalityName",
            attributeDisplayText: getLabel(CreationProject.prototype, "municipalityName"),
            values: municipalityNames
        },
        {
            attribute: "actionStatusDisplayText",
            attributeDisplayText: getLabel(CreationProject.prototype, "actionStatusDisplayText"),
            values: actionStatus
        },
        {
            attribute: "status",
            attributeDisplayText: getLabel(CreationProject.prototype, "status"),
            values: status
        },
    ]

    return (
        <Modal className="compensationNewSelectCreationProject" open={true} size="large">
            <Modal.Header>Neuschaffungsprojekt auswählen</Modal.Header>
            <Modal.Content scrolling={true}>
                <div className="content">
                    <FilterConfiguration
                        filterOptions={filterOptions}
                        initialFilters={[]}
                        onFilterAdded={(f) => dispatch({
                            type: "addFilter",
                            filter: f
                        })}
                        onFilterRemoved={(f) => dispatch({
                            type: "removeFilter",
                            id: f.id,
                        })}/>
                    <div>
                        <Form role="search">
                            <Input
                                id="searchField"
                                placeholder="Suche nach ID, Name, Gemeinde, Baugesuchsnummer, CMI-Nummer"
                                type="search"
                                name="searchField"
                                value={searchText}
                                icon={
                                    <Icon
                                        name="delete"
                                        link
                                        onClick={() => setSearchText("")}
                                    />
                                }
                                onChange={(event) => setSearchText(event.target.value)}
                                style={{ width: "800px" }}
                            />
                        </Form>
                        <Table selectable collapsing sortable compact="very" size="small">
                            <Table.Header>
                                <Table.Row verticalAlign="top">
                                    <Table.HeaderCell sorted={sortAttribute === "id" ? sortDirection : null}
                                                      onClick={() => dispatch({
                                                          type: "changeSort",
                                                          sortAttribute: "id"
                                                      })}>{getLabel(CreationProject.prototype, "id")}</Table.HeaderCell>
                                    <Table.HeaderCell sorted={sortAttribute === "municipalityName" ? sortDirection : null}
                                                      onClick={() => dispatch({
                                                          type: "changeSort",
                                                          sortAttribute: "municipalityName"
                                                      })}>{getLabel(CreationProject.prototype, "municipalityName")}</Table.HeaderCell>
                                    <Table.HeaderCell sorted={sortAttribute === "typeKey" ? sortDirection : null}
                                                      onClick={() => dispatch({
                                                          type: "changeSort",
                                                          sortAttribute: "typeKey"
                                                      })}>{getLabel(CreationProject.prototype, "fullType")}</Table.HeaderCell>
                                    <Table.HeaderCell sorted={sortAttribute === "ebageNr" ? sortDirection : null}
                                                      onClick={() => dispatch({
                                                          type: "changeSort",
                                                          sortAttribute: "ebageNr"
                                                      })}>{getLabel(CreationProject.prototype, "ebageNr")}</Table.HeaderCell>
                                    <Table.HeaderCell sorted={sortAttribute === "name" ? sortDirection : null}
                                                      onClick={() => dispatch({
                                                          type: "changeSort",
                                                          sortAttribute: "name"
                                                      })}>{getLabel(CreationProject.prototype, "name")}</Table.HeaderCell>
                                    <Table.HeaderCell sorted={sortAttribute === "status" ? sortDirection : null}
                                                      onClick={() => dispatch({
                                                          type: "changeSort",
                                                          sortAttribute: "status"
                                                      })}>{getLabel(CreationProject.prototype, "status")}</Table.HeaderCell>
                                    <Table.HeaderCell sorted={sortAttribute === "enactmentDateTimestamp" ? sortDirection : null}
                                                      onClick={() => dispatch({
                                                          type: "changeSort",
                                                          sortAttribute: "enactmentDateTimestamp"
                                                      })}>{getLabel(CreationProject.prototype, "enactmentDateDisplayText")}</Table.HeaderCell>
                                    <Table.HeaderCell textAlign="right">Restfläche [m²]</Table.HeaderCell>
                                </Table.Row>
                            </Table.Header>
                            <Table.Body>
                                {items.filter(p => projectSearchFilter(p, searchText)).filter(p => p.totalAreaAvailable > 0 && !usageProject.compensations.some(c => c.creationProjectId === p.id)).map((p) => (
                                    <Table.Row key={p.id} active={selectedCreationProject?.id === p.id}
                                               onClick={() => setSelectedCreationProject(p)}>
                                        <Table.Cell>{p.id}</Table.Cell>
                                        <Table.Cell>{p.municipalityName}</Table.Cell>
                                        <Table.Cell>{p.type}</Table.Cell>
                                        <Table.Cell>{p.ebageNr}</Table.Cell>
                                        <Table.Cell>{p.name}</Table.Cell>
                                        <Table.Cell>{p.status}</Table.Cell>
                                        <Table.Cell>{p.enactmentDateDisplayText}</Table.Cell>
                                        <Table.Cell textAlign="right">{(p.totalArea - p.totalAreaClaimed).toLocaleString()}</Table.Cell>
                                    </Table.Row>
                                ))}
                            </Table.Body>
                        </Table>
                    </div>
                </div>
            </Modal.Content>
            <Modal.Actions>
                <Button type="button" onClick={() => navigate(-1)}>Abbrechen</Button>
                <Button
                    type="button"
                    disabled={selectedCreationProject === undefined}
                    onClick={() => navigate(`${selectedCreationProject?.id}`, { preventScrollReset: true })}
                >
                    Weiter
                </Button>
            </Modal.Actions>
        </Modal>
    )
        ;
}